/**
 * Map cumulative journey JSON (from getJourney) to proposal tab / section progress.
 * Backend stores one cumulative key per proposal screen: Stage 8.0 … 8.5 ProposalFormScreen*.
 */

import { PROPOSAL_UI_TABS, PROPOSAL_STAGE_BY_STEP } from '../../api/journeyService';

/** @type {Map<string, string>} journeyStage string → stepId */
const JOURNEY_STAGE_TO_STEP_ID = new Map();
for (const tab of PROPOSAL_UI_TABS.tabs) {
  for (const sec of tab.sections) {
    JOURNEY_STAGE_TO_STEP_ID.set(sec.journeyStage, sec.stepId);
  }
}

const STEP_IDS_ORDERED = PROPOSAL_UI_TABS.tabs.flatMap((t) => t.sections.map((s) => s.stepId));

/**
 * Infer proposal stepId from a cumulative journey object key (exact or fuzzy).
 * @param {string} cumulativeKey
 * @returns {string|null}
 */
export function cumulativeKeyToProposalStepId(cumulativeKey) {
  if (cumulativeKey == null) return null;
  const k = String(cumulativeKey).trim();
  if (JOURNEY_STAGE_TO_STEP_ID.has(k)) {
    return JOURNEY_STAGE_TO_STEP_ID.get(k);
  }
  const lower = k.toLowerCase();
  if (!lower.includes('proposal') && !/8\.\d/.test(lower)) return null;
  if (lower.includes('8.0')) return 'proposer-personal';
  if (lower.includes('8.1')) return 'proposer-bank';
  if (lower.includes('8.2')) return 'primaryAnnuitant';
  if (lower.includes('8.3')) return 'secondaryAnnuitant';
  if (lower.includes('8.4')) return 'nominee';
  if (lower.includes('8.5')) return 'declaration';
  return null;
}

/**
 * Build Redux `proposalData` shape from parsed cumulative stages (for resume/hydration).
 * @param {Record<string, unknown>|null} stagesObject
 * @returns {Record<string, unknown>|null}
 */
export function buildProposalProgressFromCumulative(stagesObject) {
  if (!stagesObject || typeof stagesObject !== 'object') return null;
  const progress = {
    hydratedFromJourney: true,
    lastUpdatedAt: new Date().toISOString(),
  };
  let any = false;
  for (const key of Object.keys(stagesObject)) {
    const stepId = cumulativeKeyToProposalStepId(key);
    if (stepId) {
      progress[stepId] = true;
      any = true;
    }
  }
  return any ? progress : null;
}

/**
 * Group cumulative stage payloads by top tab (lifeAssured | nominee | medical) for UI/debug.
 * Values are raw blocks from cumulative JSON (may be nested wrapper objects).
 * @param {Record<string, unknown>|null} stagesObject
 * @returns {{ lifeAssured: Record<string, unknown>, nominee: Record<string, unknown>, medical: Record<string, unknown> }}
 */
export function extractProposalStagesByTopTab(stagesObject) {
  const out = {};
  for (const tab of PROPOSAL_UI_TABS.tabs) {
    out[tab.id] = {};
  }
  if (!stagesObject || typeof stagesObject !== 'object') return out;

  for (const tab of PROPOSAL_UI_TABS.tabs) {
    for (const sec of tab.sections) {
      const block = findStageBlock(stagesObject, sec.journeyStage);
      if (block != null) {
        out[tab.id][sec.stepId] = unwrapStagePayload(block);
      }
    }
  }
  return out;
}

/**
 * Ordered list of proposal step ids (Life Assured sections → Nominee → Medical sections).
 */
export function getProposalStepIdsOrdered() {
  return [...STEP_IDS_ORDERED];
}

/**
 * Reverse lookup: stepId → backend cumulative key label.
 * @param {string} stepId
 * @returns {string|undefined}
 */
export function proposalStepIdToJourneyStage(stepId) {
  return PROPOSAL_STAGE_BY_STEP[stepId];
}

function findStageBlock(stagesObject, journeyStage) {
  if (!stagesObject || typeof stagesObject !== 'object') return null;
  if (journeyStage != null && stagesObject[journeyStage] != null) {
    return stagesObject[journeyStage];
  }
  const keys = Object.keys(stagesObject);
  if (journeyStage) {
    const needle = journeyStage.toLowerCase();
    const hit = keys.find((k) => k.toLowerCase() === needle);
    if (hit) return stagesObject[hit];
    const num = journeyStage.match(/8\.\d/);
    if (num) {
      const fuzzy = keys.find((k) => k.includes(num[0]) && /proposal/i.test(k));
      if (fuzzy) return stagesObject[fuzzy];
    }
  }
  return null;
}

function unwrapStagePayload(block) {
  if (block == null) return null;
  if (typeof block === 'string') {
    try {
      return JSON.parse(block);
    } catch {
      return { _raw: block };
    }
  }
  if (typeof block === 'object' && !Array.isArray(block)) {
    const keys = Object.keys(block);
    if (keys.length === 1 && keys[0].startsWith('Stage ')) {
      return block[keys[0]];
    }
  }
  return block;
}

/**
 * Collect stage blobs from journeyData.stages / currentStageData (FormScreen + short aliases).
 * @param {object|null|undefined} journeyData
 * @param {...string} stageNames
 * @returns {Record<string, unknown>}
 */
export function mergeProposalStagePayloads(journeyData, ...stageNames) {
  const stages = {
    ...(journeyData?.stages && typeof journeyData.stages === 'object'
      ? journeyData.stages
      : {}),
    ...(journeyData?.currentStageData &&
    typeof journeyData.currentStageData === 'object'
      ? journeyData.currentStageData
      : {}),
  };
  const merged = {};
  for (const stageName of stageNames) {
    const block = unwrapStagePayload(findStageBlock(stages, stageName));
    if (block && typeof block === 'object' && !Array.isArray(block)) {
      Object.assign(merged, block);
    }
  }
  return merged;
}

/** Proposer personal (8.0) ∪ bank (8.1) — required after step-owned persistence. */
export function mergeProposerStagesFromJourney(journeyData) {
  return mergeProposalStagePayloads(
    journeyData,
    'Stage 8.0 ProposalFormScreen1',
    'Stage 8.0 Proposal',
    'Stage 8.1 ProposalFormScreen2',
    'Stage 8.1 Proposal',
  );
}

/** Primary (8.2 pa*) ∪ secondary (8.3 sa*) annuitant. */
export function mergeAnnuitantStagesFromJourney(journeyData) {
  return mergeProposalStagePayloads(
    journeyData,
    'Stage 8.2 ProposalFormScreen3',
    'Stage 8.2 Proposal',
    'Stage 8.3 ProposalFormScreen4',
    'Stage 8.3 Proposal',
  );
}

/** Declaration stage (8.5) — FormScreen6/7/8 + short. */
export function mergeDeclarationStagesFromJourney(journeyData) {
  return mergeProposalStagePayloads(
    journeyData,
    'Stage 8.5 ProposalFormScreen6',
    'Stage 8.5 ProposalFormScreen7',
    'Stage 8.5 ProposalFormScreen8',
    'Stage 8.5 Proposal',
  );
}

/** Nominee stage (8.4). */
export function mergeNomineeStagesFromJourney(journeyData) {
  return mergeProposalStagePayloads(
    journeyData,
    'Stage 8.4 ProposalFormScreen5',
    'Stage 8.4 Proposal',
  );
}

/**
 * Merge owned-field stage blobs into Redux stepData tabs after resume.
 * Also works when stage blobs lack `tab` (maps by stage key via cumulativeKeyToProposalStepId).
 * @param {Record<string, unknown>|null} stagesObject
 * @returns {{ proposer: object, annuitant: object, nominee: object, declaration: object }}
 */
export function mergeProposalStepDataFromCumulative(stagesObject) {
  const out = {
    proposer: {},
    annuitant: {},
    nominee: {},
    declaration: {},
  };
  if (!stagesObject || typeof stagesObject !== 'object') return out;

  const byTab = {
    proposer: {},
    annuitant: {},
    nominee: {},
    declaration: {},
  };

  for (const [key, value] of Object.entries(stagesObject)) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
    const unwrapped = unwrapStagePayload(value);
    if (!unwrapped || typeof unwrapped !== 'object' || Array.isArray(unwrapped)) {
      continue;
    }
    let tab = unwrapped.tab;
    if (tab === 'lifeAssured') tab = 'proposer';
    if (!tab) {
      const stepId = cumulativeKeyToProposalStepId(key);
      if (stepId === 'proposer-personal' || stepId === 'proposer-bank') {
        tab = 'proposer';
      } else if (
        stepId === 'primaryAnnuitant' ||
        stepId === 'secondaryAnnuitant'
      ) {
        tab = 'annuitant';
      } else if (stepId === 'nominee') {
        tab = 'nominee';
      } else if (stepId === 'declaration') {
        tab = 'declaration';
      }
    }
    if (!tab || !byTab[tab]) continue;
    byTab[tab][key] = unwrapped;
  }

  // Numeric stage order so later owned steps overlay earlier ones.
  const sortKeys = (obj) =>
    Object.keys(obj).sort((a, b) => {
      const na = parseFloat((String(a).match(/(\d+(?:\.\d+)?)/) || [])[1] || '0');
      const nb = parseFloat((String(b).match(/(\d+(?:\.\d+)?)/) || [])[1] || '0');
      return na - nb;
    });

  for (const tab of Object.keys(byTab)) {
    const merged = {};
    for (const k of sortKeys(byTab[tab])) {
      Object.assign(merged, byTab[tab][k]);
    }
    out[tab] = merged;
  }
  return out;
}

